import { extractSchemaFields } from '@/hooks/use-data-schema-versioning/index';
import {
	diffAgainstBase,
	diffRootMetadata,
	diffCustomValidators,
} from '../../dsv-schema-utils';
import { diffRulesForCompare } from './rule-compare';
import { diffFieldOrderForCompare } from './field-order-compare';

/**
 * Compare two persisted JSON Schemas. `left` is the base, `right` is the
 * target. Field/metadata/validator diffs reuse the shared primitives
 * (extracting field shape from the target). Rule and field-order diffs use
 * Compare-layer helpers that ignore positional/membership noise.
 *
 * @param {{ leftSchema: object, rightSchema: object }} input
 */
export function buildComparison({ leftSchema, rightSchema }) {
	const left = leftSchema || {};
	const right = rightSchema || {};

	const fields = diffAgainstBase(extractSchemaFields(right), left);
	const rules = diffRulesForCompare(left, right);
	const rootMeta = diffRootMetadata(right, left);
	const customValidators = diffCustomValidators(right, left);
	const fieldOrder = diffFieldOrderForCompare(left, right);

	const count = (d) => (d.added.length + d.modified.length + d.removed.length);
	const ruleChanges = count(rules);
	const metaChanges = count(rootMeta);
	const validatorChanges = count(customValidators);
	const orderChanged = fieldOrder !== null;

	const summary = {
		added: fields.added.length,
		modified: fields.modified.length,
		removed: fields.removed.length,
		ruleChanges,
		metaChanges,
		validatorChanges,
		orderChanged,
	};
	summary.hasChanges = Boolean(
		summary.added || summary.modified || summary.removed ||
		ruleChanges || metaChanges || validatorChanges || orderChanged,
	);

	return { fields, rules, rootMeta, customValidators, fieldOrder, summary };
}

function fieldDepth(displayPath) {
	return (displayPath.match(/\./g) || []).length;
}

function rowFromField(field) {
	return {
		displayPath: field.displayPath,
		dataElementName: field.dataElementName,
		dataType: field.dataType,
		mandatory: !!field.mandatory,
	};
}

// Flatten an extractSchemaFields tree into { displayPath -> rowData }.
function flattenFieldTree(fields, parentPath, acc) {
	for (const f of fields) {
		const displayPath = parentPath ? `${parentPath}.${f.dataElementName}` : f.dataElementName;
		acc.set(displayPath, rowFromField({ ...f, displayPath }));
		if (Array.isArray(f.children) && f.children.length > 0) {
			flattenFieldTree(f.children, displayPath, acc);
		}
	}
	return acc;
}

/**
 * Build ordered side-by-side rows for the Fields dimension.
 * Each row: { displayPath, depth, status, left, right, changes }.
 * status: 'added' | 'removed' | 'modified' | 'unchanged'.
 */
export function buildSideBySideRows(comparison, { onlyChanges, leftSchema, rightSchema }) {
	const { added, modified, removed } = comparison.fields;

	const changedRows = [];
	for (const f of removed) {
		changedRows.push({ displayPath: f.displayPath, depth: fieldDepth(f.displayPath), status: 'removed', left: rowFromField(f), right: null, changes: [] });
	}
	for (const f of added) {
		changedRows.push({ displayPath: f.displayPath, depth: fieldDepth(f.displayPath), status: 'added', left: null, right: rowFromField(f), changes: [] });
	}
	for (const m of modified) {
		const path = m.field.displayPath;
		changedRows.push({ displayPath: path, depth: fieldDepth(path), status: 'modified', left: rowFromField(m.field), right: rowFromField(m.field), changes: m.changes });
	}

	if (onlyChanges) {
		return changedRows.sort((a, b) => a.displayPath.localeCompare(b.displayPath));
	}

	// Full mode: union of all paths from both schemas; changed paths use the
	// rows above, the rest are 'unchanged'.
	const leftMap = flattenFieldTree(extractSchemaFields(leftSchema || {}), '', new Map());
	const rightMap = flattenFieldTree(extractSchemaFields(rightSchema || {}), '', new Map());
	const changedByPath = new Map(changedRows.map(r => [r.displayPath, r]));

	const allPaths = new Set([...leftMap.keys(), ...rightMap.keys(), ...changedByPath.keys()]);
	const rows = [];
	for (const path of allPaths) {
		if (changedByPath.has(path)) {
			rows.push(changedByPath.get(path));
			continue;
		}
		rows.push({
			displayPath: path,
			depth: fieldDepth(path),
			status: 'unchanged',
			left: leftMap.get(path) || null,
			right: rightMap.get(path) || null,
			changes: [],
		});
	}
	return rows.sort((a, b) => a.displayPath.localeCompare(b.displayPath));
}
